import * as FileSaver from 'file-saver';
import { copyToClipboard } from '../../utils/helper';
import connectPrinter from '../../utils/connectPrinter';
import { formatDateTime } from '../../utils/dataFormatter';
import { DATE_TIME_TYPES } from '../../constants/dataTimeTypes';
import {
    validateSenderName,
    validateReceiverName,
    validateMobileNumber,
    validateMessage,
} from '../../utils/validations';


//Filter status list to only include allowed status keys
export const getAllowedStatuses = (statusList, allowedKeys) => {
    return statusList.filter((item) => allowedKeys.includes(item.key));
};

// Generate ZPL (Zebra Programming Language) code for QR codes
export const generateZplCode = (voucherIds) => {
    let zpl = '';
    let index = 1;

    voucherIds.forEach((id) => {
        if (index % 3 === 1) {
            zpl += `^XA
^MD22
^PW799
^LL799

^FO38,6^BQN,2,6^FDQA,https://thyaga.lk/checkbalance/${id}^FS
^FO70,220^A0N,28,28^FD${id}^FS`;
        } else if (index % 3 === 2) {
            zpl += `^FO302,6^BQN,2,6^FDQA,https://thyaga.lk/checkbalance/${id}^FS
^FO334,220^A0N,28,28^FD${id}^FS`;
        } else {
            zpl += `^FO566,6^BQN,2,6^FDQA,https://thyaga.lk/checkbalance/${id}^FS
^FO600,220^A0N,28,28^FD${id}^FS

^XZ`;
        }

        index++;
    });

    if (index % 3 !== 1) {
        zpl += `^XZ`;
    }

    return zpl;
};

// Send ZPL code to Zebra printer
export const sendToPrinter = async (zplCode) => {
    try {
        const device = await connectPrinter();
        const encoder = new TextEncoder();
        const data = encoder.encode(zplCode);
        await device.transferOut(1, data);
    } catch (error) {
        console.error('Printer connection error:', error);
        throw error;
    }
};

// Decode and save Base64 PDF to file
export const savePdfFile = (base64Data, fileName) => {
    try {
        const decodedPdfData = Buffer.from(base64Data, 'base64');
        const blob = new Blob([decodedPdfData], { type: 'application/pdf' });
        FileSaver.saveAs(blob, fileName);
    } catch (error) {
        console.error('Error saving PDF file:', error);
        throw error;
    }
};

// Generate PDF from API response and save it
export const generateAndSavePdf = (rawPdfResponse, prefix = '') => {
    const date = new Date();
    const fileName = prefix
        ? `${prefix}_${formatDateTime(date, DATE_TIME_TYPES.ISO_DATE_TIME)}`
        : formatDateTime(date, DATE_TIME_TYPES.ISO_DATE_TIME);

    savePdfFile(rawPdfResponse, fileName);
};

// Create carrier print request from voucher IDs
export const createCarrierPrintRequest = (voucherIds, to, from, body) => {
    return voucherIds.map((id) => ({
        voucherId: id,
        to,
        from,
        body,
    }));
};

// Copy text to clipboard and show temporary feedback
export const copyToClipboardWithFeedback = (text, setState, duration = 2000) => {
    copyToClipboard(text);
    setState({ copied: true });

    setTimeout(() => {
        setState({ copied: false });
    }, duration);
};

// Validate edit field based on field type
export const validateEditFieldValue = (field, value) => {
    switch (field) {
        case 'sender':
            return validateSenderName(value);
        case 'receiver':
            return validateReceiverName(value);
        case 'mobile':
            return validateMobileNumber(value);
        case 'message':
            return validateMessage(value);
        default:
            return { isValid: true, error: '' };
    }
};

// Get initial value for edit field from drawer data
export const getEditFieldInitialValue = (field, drawerData) => {
    const fieldMap = {
        sender: 'sender',
        receiver: 'ownerName',
        mobile: 'ownerMobile',
        message: 'message',
    };

    const dataKey = fieldMap[field];
    return dataKey ? drawerData[dataKey] : '';
};

// Get state key name for edit field
export const getEditStateKeyForField = (field) => {
    const stateKeyMap = {
        sender: 'editSenderName',
        receiver: 'editReceiverName',
        mobile: 'editReceiverMobile',
        message: 'editMessage',
    };

    return stateKeyMap[field] || '';
};

// Build update payload for edit field
export const buildEditPayload = (field, value) => {
    const basePayload = {
        senderName: null,
        receiverName: null,
        message: null,
        ownerMobile: null,
    };

    const fieldMap = {
        sender: 'senderName',
        receiver: 'receiverName',
        mobile: 'ownerMobile',
        message: 'message',
    };

    const payloadKey = fieldMap[field];
    if (payloadKey) {
        return { ...basePayload, [payloadKey]: value };
    }

    return basePayload;
};

// Check if searching by receiver number
export const isSearchingByReceiverNumber = (queryParams, searchId) => {
    return (queryParams?.searchId || searchId) === 'mobile_no';
};

// Format amount with thousand separators
export const formatAmount = (amount) => {
    if (!amount) return '0.00';
    return amount
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

// Truncate text with ellipsis if it exceeds max length
export const truncateText = (text, maxLength = 20) => {
    if (!text) return { truncated: false, text: '' };

    return {
        truncated: text.length > maxLength,
        text: text.length > maxLength ? text.substring(0, maxLength) : text,
    };
};

// Check if a value is empty or null
export const isEmpty = (value) => {
    return value === null || value === undefined || value === '';
};

// Filter out empty values from content array
export const filterEmptyContent = (contentArray) => {
    return contentArray.filter(
        (item) => item.value !== null && item.value !== undefined && item.value !== ''
    );
};
